import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import gsap from 'gsap';
import { useMutation } from 'react-query';

import classnames from 'utils/classnames';
import { stringToCurrency } from 'utils/currency';
import { getAdded, getModified, getRemoved } from 'utils/product';
import sanfordApi, {
  SFManageSubscriptionRequest,
  SFShippingAddressRequest,
  SFUpdateSubscriptionRequest,
} from 'lib/sanford-api';
import { CustomerAddressData, ProductData } from 'lib/bigcommerce/types';

import CheckmarkIcon from 'components/icons/CheckmarkIcon';
import AlertIcon from 'components/icons/AlertIcon';
import CloseModalIcon from 'components/icons/CloseModalIcon';
import { FillButton, OutlineButton } from 'components/Button';
import HorizontalLine from 'components/ui/HorizontalLine';
import { SFSubscription, SFSubscriptionProduct } from 'pages/api/account/subscriptions';
import Badge from './badge';
import SubscriptionOrder from './subscriptionOrder';
import SubscriptionAddItemModal from './subscriptionAddItemModal';
import SpinnerIcon from 'components/icons/SpinnerIcon';
import { formatDate } from 'utils/date';
import ReOrderIcon from 'components/icons/ReOrderIcon';
import BinIcon from 'components/icons/BinIcon';
import CrossIcon from 'components/icons/CrossIcon';
import CheckboxIcon from 'components/icons/CheckboxIcon';
import { convertProduct, getAddressesModified } from 'utils/subscription';
import SubscriptionDateFrequencyModal from './subscriptionResumeModal';
import ConfirmModal from './subscriptionConfirmModal';
import { ModalProps, SubscriptionAddressHandlerProps, SubscriptionItemHandlerProps } from './index';
import {
  AddressTypes,
  SubscriptionActions,
  SubscriptionAlerts,
  SubscriptionItemActions,
} from 'lib/constants';
import Select from 'components/form/Select';
import EditIcon from 'components/icons/EditIcon';
import AlertModal from './subscriptionAlertModal';

type SubscriptionItemProps = {
  products: ProductData[];
  baseSubscription: SFSubscription;
  subscription: SFSubscription;
  availableAddresses: CustomerAddressData[];
  handleSubscriptionAddress?: (props: SubscriptionAddressHandlerProps) => void;
  resetSubscription: () => void;
  resetSubscriptionAlias: () => void;
  handleUpdate?: (subscription: SFSubscription) => void;
  modal: ModalProps;
  handleModal: (args: ModalProps) => void;
  handleRefetch: () => void;
  handleSubscriptionItem: (
    props: Omit<SubscriptionItemHandlerProps, 'item'> & {
      item?: Omit<SFSubscriptionProduct, 'id'>;
    }
  ) => void;
};

type SubscriptionChanges = {
  modified: Array<SFSubscription['items'][0] | string>;
  added: Array<SFSubscription['items'][0]>;
  removed: Array<SFSubscription['items'][0]>;
};

export type ManageSubscriptionProps = {
  action: SubscriptionActions.ACTIVATE_SUBSCRIPTION | SubscriptionActions.CHANGE_DATE_FREQUENCY;
  manageSubscription: SFSubscription;
  deliveryDate: string;
  subscriptionFrequency: string;
};

const SubscriptionItem: React.FC<SubscriptionItemProps> = (props) => {
  const {
    products,
    baseSubscription,
    subscription,
    availableAddresses,
    handleSubscriptionAddress,
    resetSubscription,
    resetSubscriptionAlias,
    modal,
    handleModal,
    handleSubscriptionItem,
    handleRefetch,
  } = props;

  const compareAddresses = (bcList, dbAddress) => {
    const getAddress = bcList.find(
      (address) =>
        address.address1 === dbAddress.street_1 &&
        address.address2 === dbAddress.street_2 &&
        address.city === dbAddress.city &&
        address.stateOrProvince === dbAddress.state &&
        address.postalCode === dbAddress.zip &&
        address.country === dbAddress.country
    );
    return getAddress ? getAddress : dbAddress;
  };

  const findAddress = (type: AddressTypes.SHIPPING | AddressTypes.BILLING) =>
    availableAddresses?.length > 0 && subscription?.[`${type}_address`]?.id
      ? compareAddresses(availableAddresses, subscription?.[`${type}_address`])
      : null;

  const [processing, setProcessing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [canAddItem, setCanAddItem] = useState(false);
  const [getChanges, setGetChanges] = useState<SubscriptionChanges>(null);
  const [subscriptionEditName, setSubscriptionEditName] = useState<boolean>(false);
  const [subscriptionEditNameSuccess, setSubscriptionEditNameSuccess] = useState<boolean>(false);

  const subscriptionAliasRef = useRef(null);

  const changesBlockRef = useRef(null);

  const active = !!subscription?.active;

  const theme = active ? 'Gold' : 'Blue';

  const subscriptionMutation = useMutation(
    (data) => sanfordApi.updateCustomerSubscription(subscription?.id, data),
    {
      onMutate: async (data: SFUpdateSubscriptionRequest) => {
        setProcessing(true);
      },
      onError: (err, variables, context) => {},
      onSuccess: (data) => {
        handleRefetch();
      },
      onSettled: () => {
        setTimeout(() => {
          setProcessing(false);
        }, 1000);
      },
      mutationKey: `updateSubscription-${subscription?.id}`,
    }
  );

  const subscriptionManageMutation = useMutation(
    (data) => sanfordApi.manageCustomerSubscription(subscription?.id, data),
    {
      onMutate: async (data: SFManageSubscriptionRequest) => {
        setProcessing(true);
      },
      onError: (err, variables, context) => {},
      onSuccess: (data) => {
        setSubscriptionEditNameSuccess(true);
        handleRefetch();
      },
      onSettled: () => {
        setTimeout(() => {
          setProcessing(false);
        }, 1000);
        setTimeout(() => {
          setSubscriptionEditNameSuccess(false);
          setSubscriptionEditName(false);
        }, 1500);
      },
      mutationKey: `manageSubscription-${subscription?.id}`,
    }
  );

  const subscriptionShippingAddressMutation = useMutation(
    (data) => sanfordApi.getCustomerShippingCost(data),
    {
      onMutate: async (data: SFShippingAddressRequest) => {
        setProcessing(true);
      },
      onError: (err, variables, context) => {},
      onSuccess: (data, variables, context) => {
        const shippingCostResponse = data?.data?.result;
        handleSubscriptionAddress({
          action: AddressTypes.SHIPPING,
          subscription: variables.subscription,
          address: variables.address,
          shippingCost: shippingCostResponse?.settings?.rate as string,
        });
      },
      onSettled: () => {
        setTimeout(() => {
          setProcessing(false);
        }, 1000);
      },
      mutationKey: `shippingCost-${subscription?.id}`,
    }
  );

  const handleResetSubscriptionAlias = () => {
    resetSubscriptionAlias();
    setSubscriptionEditName(false);
    subscriptionAliasRef.current.innerText = subscription?.alias || subscription?.label;
  };

  const handleAddItemsToSubscription = (addProducts: ProductData[]) => {
    handleSubscriptionItem({
      action: SubscriptionItemActions.ADD,
      subscription,
      items: addProducts.map((item) => convertProduct(item)),
    });
  };

  const handleManageSubscription = ({
    action,
    deliveryDate,
    subscriptionFrequency,
  }: ManageSubscriptionProps) => {
    if (action === SubscriptionActions.ACTIVATE_SUBSCRIPTION) {
      subscriptionManageMutation.mutate({
        action: SubscriptionActions.RESUME,
        data: {
          deliveryDate,
          subscriptionFrequency,
        },
      });
    }
    if (action === SubscriptionActions.CHANGE_DATE_FREQUENCY) {
      subscriptionManageMutation.mutate({
        action: SubscriptionActions.CHANGE_DATE_FREQUENCY,
        data: {
          deliveryDate,
          subscriptionFrequency,
        },
      });
    }
    handleModal({
      id: subscription.id,
      type: action,
      show: false,
    });
  };

  const handleConfirmAction = (confirmSubscription: SFSubscription, action: string) => {
    if (modal?.action === SubscriptionActions.CANCEL) {
      if (action === SubscriptionActions.CONFIRM) {
        subscriptionManageMutation.mutate({
          action: SubscriptionActions.ARCHIVE,
        });
      }
    }
    if (modal?.action === SubscriptionActions.PAUSE) {
      if (action === SubscriptionActions.CONFIRM) {
        subscriptionManageMutation.mutate({
          action: SubscriptionActions.PAUSE,
        });
      }
    }

    handleModal({
      id: confirmSubscription.id,
      type: SubscriptionActions.CONFIRM_ACTION,
      show: false,
    });
  };

  const getAvailableItems =
    products &&
    products.filter(
      (product) => !subscription?.items.map((item) => item.productId).includes(product.entityId)
    );

  useEffect(() => {
    if (baseSubscription && subscription) {
      if (!_.isEqual(baseSubscription, subscription)) {
        setHasChanges(true);
      } else {
        setHasChanges(false);
      }
      setGetChanges({
        added: getAdded(baseSubscription, subscription),
        modified: [
          ...getModified(baseSubscription, subscription),
          ...getAddressesModified(baseSubscription, subscription),
        ].filter((x) => x),
        removed: getRemoved(baseSubscription, subscription),
      });
      setCanAddItem(getAvailableItems?.length > 0 && active);
    }
  }, [subscription, subscription?.items]);

  useEffect(() => {
    const changesBlock = changesBlockRef.current;
    const revealBlock = gsap
      .timeline()
      .fromTo(
        changesBlock,
        { y: -30, height: 0 },
        {
          height: 'auto',
          y: 0,
          duration: 0.4,
          ease: 'power3.out',
        }
      )
      .fromTo(
        changesBlock,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
        }
      );
    if (hasChanges) {
      revealBlock.play();
    } else {
      revealBlock.reverse();
    }
    return () => {
      gsap.killTweensOf([changesBlock]);
    };
  }, [hasChanges]);

  return (
    <>
      {modal?.show &&
        modal?.type === SubscriptionActions.ADD_ITEM &&
        modal?.id === subscription?.id && (
          <div
            className={
              'fixed inset-0 top-20 z-50 flex h-screen w-screen flex-col overflow-hidden rounded border bg-sf-gray-100 bg-opacity-75 md:top-auto md:content-center md:items-center md:justify-center md:border-none'
            }
          >
            <SubscriptionAddItemModal
              products={getAvailableItems}
              subscription={subscription}
              handleModal={handleModal}
              handleAddItemsToSubscription={handleAddItemsToSubscription}
            />
          </div>
        )}

      {modal?.show &&
        (modal?.type === SubscriptionActions.ACTIVATE_SUBSCRIPTION ||
          modal?.type === SubscriptionActions.CHANGE_DATE_FREQUENCY) &&
        modal?.id === subscription?.id && (
          <div
            className={
              'fixed inset-0 top-20 z-50 flex h-screen w-screen flex-col overflow-hidden rounded border bg-sf-gray-100 bg-opacity-75 md:top-auto md:content-center md:items-center md:justify-center md:border-none'
            }
          >
            {availableAddresses?.length > 0 ? (
              <SubscriptionDateFrequencyModal
                action={modal?.type}
                subscription={subscription}
                handleModal={handleModal}
                handleManageSubscription={handleManageSubscription}
              />
            ) : (
              <AlertModal message={SubscriptionAlerts.NO_ADDRESS} handleModal={handleModal} />
            )}
          </div>
        )}

      {modal?.show &&
        modal?.type === SubscriptionActions.CONFIRM_ACTION &&
        modal?.id === subscription?.id && (
          <div
            className={
              'fixed inset-0 top-20 z-50 flex h-screen w-screen flex-col overflow-hidden rounded border bg-sf-gray-100 bg-opacity-75 md:top-auto md:content-center md:items-center md:justify-center md:border-none'
            }
          >
            <ConfirmModal
              subscription={subscription}
              action={modal?.action}
              handleConfirmAction={handleConfirmAction}
            />
          </div>
        )}

      <div className={'relative'}>
        {processing && (
          <div
            className={
              'absolute inset-0 flex h-full w-full flex-col content-center items-center justify-center overflow-hidden bg-black bg-opacity-10'
            }
          >
            <SpinnerIcon className="m-auto animate-spin" />
          </div>
        )}
        <div className="rounded bg-sf-gray-100 p-6">
          <div className="space-y-4">
            <div className="flex flex-col">
              <div className={'flex justify-center'}>
                <div
                  className={`${classnames({
                    invisible: !subscriptionEditName,
                    underline: subscriptionEditName,
                  })} relative flex w-full items-center justify-between gap-x-4 underline-offset-4 focus:outline-none`}
                >
                  <div
                    ref={subscriptionAliasRef}
                    contentEditable={subscriptionEditName}
                    className="text-md visible font-medium uppercase focus:outline-none active:outline-none"
                  >
                    {subscription?.alias || subscription?.label}
                  </div>
                  {!subscriptionEditNameSuccess && (
                    <div onClick={handleResetSubscriptionAlias}>
                      <CloseModalIcon className={'h-6 w-6'} />
                    </div>
                  )}
                  {subscriptionEditNameSuccess && (
                    <div>
                      <CheckmarkIcon className={'h-6 w-6'} />
                    </div>
                  )}
                </div>
              </div>
              <div className={'flex justify-center'}>
                <div className="cursor-pointer pt-2 text-sm font-medium text-sf-blue-700 underline">
                  {!subscriptionEditName && (
                    <div onClick={() => setSubscriptionEditName(!subscriptionEditName)}>
                      Edit name
                    </div>
                  )}
                  {subscriptionEditName && (
                    <div
                      onClick={() =>
                        subscriptionAliasRef.current.innerText.length > 0
                          ? subscriptionManageMutation.mutate({
                              action: SubscriptionActions.ALIAS,
                              data: {
                                alias:
                                  subscriptionAliasRef.current.innerText || subscription?.label,
                              },
                            })
                          : (subscriptionAliasRef.current.innerText = subscription?.label)
                      }
                      className="cursor-pointer text-sm font-medium text-sf-blue-700 underline md:ml-auto"
                    >
                      Save
                    </div>
                  )}
                </div>
              </div>
              <div className={'flex justify-center py-4'}>
                <Badge color={theme}> {subscription?.active ? 'Active' : 'Paused'}</Badge>
              </div>
              <div className="flex justify-center py-2">
                <div
                  className={classnames('rounded-full px-6', {
                    'bg-sf-gold-300 bg-opacity-15': subscription?.active,
                    'bg-[#DCDFE5]': !subscription?.active,
                  })}
                >
                  <div className={'inline-flex items-center space-x-4 py-3'}>
                    {subscription?.active ? (
                      <CheckmarkIcon className="text-sf-gold-300" />
                    ) : (
                      <AlertIcon />
                    )}
                    <div>
                      Frequency <span className="font-medium">{subscription?.frequency}</span>
                      {active && (
                        <div className={'flex space-x-2'}>
                          <p className="text-xs leading-tight md:text-sm">Next delivery</p>
                          <p className="text-xs font-medium leading-tight md:text-sm">
                            {subscription?.deliveryDate
                              ? formatDate(subscription?.deliveryDate)
                              : 'N/A'}
                          </p>
                        </div>
                      )}
                    </div>
                    {active && (
                      <div
                        className={'cursor-pointer'}
                        onClick={() =>
                          handleModal({
                            id: subscription.id,
                            type: SubscriptionActions.CHANGE_DATE_FREQUENCY,
                            show: true,
                          })
                        }
                      >
                        <EditIcon className={'h-4 w-4'} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <HorizontalLine />
            <div className={'flex items-center justify-between py-2'}>
              <p className="pt-1 text-base leading-tight">Order No. {subscription?.id}</p>
              <p className="text-sm leading-none">
                Items to purchase{' '}
                <span className={'font-bold'}>({subscription?.items?.length})</span>
              </p>
            </div>
            <div className="space-y-6">
              {subscription?.items.map((item, index) => (
                <div key={item?.name}>
                  {item && (
                    <SubscriptionOrder
                      product={item}
                      editable={active}
                      canDelete={subscription?.items?.length > 1}
                      moreItem={() =>
                        handleSubscriptionItem({
                          action: SubscriptionItemActions.MORE,
                          subscription,
                          item,
                        })
                      }
                      lessItem={() =>
                        handleSubscriptionItem({
                          action: SubscriptionItemActions.LESS,
                          subscription,
                          item,
                        })
                      }
                      removeItem={() =>
                        handleSubscriptionItem({
                          action: SubscriptionItemActions.REMOVE,
                          subscription,
                          item,
                        })
                      }
                    />
                  )}
                </div>
              ))}
              <HorizontalLine />
              <div
                className={`space-y-2 ${classnames({
                  'opacity-40': !active,
                })}`}
              >
                <div className="text-center">
                  <span className="font-medium">Total</span>{' '}
                  {stringToCurrency(subscription?.total.toString(), '')}
                  {subscription?.shippingCost && (
                    <div className="text-xs">
                      Shipping cost of{` `}
                      {stringToCurrency(subscription?.shippingCost, '')} included{' '}
                    </div>
                  )}
                </div>
                <div className={'flex justify-center'}>
                  <div
                    onClick={() => {
                      if (!!!canAddItem) return;
                      handleModal({
                        id: subscription.id,
                        type: SubscriptionActions.ADD_ITEM,
                        show: true,
                      });
                    }}
                  >
                    <OutlineButton buttonSize="small" disabled={!!!canAddItem}>
                      <span className="normal-case">Add products to your subscription</span>
                    </OutlineButton>
                  </div>
                </div>
              </div>
            </div>
            <HorizontalLine />
            <div
              className={`flex flex-col items-center justify-between space-x-2 md:flex-row ${classnames(
                {
                  'opacity-40': !active,
                }
              )}`}
            >
              <div className="w-full px-1 py-2 md:w-1/2">
                <span className={'whitespace-nowrap'}>Shipping address:</span>
              </div>
              <div className={'w-full md:w-1/2'}>
                {availableAddresses?.length <= 0 && (
                  <span className={'text-sm'}>{`${subscription.shipping_address.street_1}`}</span>
                )}
                {availableAddresses?.length > 0 && findAddress(AddressTypes.SHIPPING)?.id && (
                  <Select
                    id={'subscriptionShippingAddress'}
                    label={'Address'}
                    value={findAddress(AddressTypes.SHIPPING)?.id}
                    onBlur={() => null}
                    disabled={!active}
                    onChange={(address) => {
                      subscriptionShippingAddressMutation.mutate({
                        subscription,
                        address: availableAddresses[address.target.selectedIndex],
                      });
                    }}
                    className={`cursor-pointer appearance-none bg-transparent text-sm hover:shadow-md`}
                  >
                    {findAddress(AddressTypes.SHIPPING)?.street_1 && (
                      <option
                        disabled={true}
                        key={findAddress(AddressTypes.SHIPPING)?.id}
                        value={findAddress(AddressTypes.SHIPPING)?.id}
                      >
                        {`${findAddress(AddressTypes.SHIPPING)?.street_1}, ${
                          findAddress(AddressTypes.SHIPPING)?.street_2
                        }`}
                      </option>
                    )}
                    {availableAddresses?.map((address, index) => {
                      return (
                        <option key={address.id} value={address.id}>
                          {`${address.address1}, ${address.address2}`}
                        </option>
                      );
                    })}
                  </Select>
                )}
              </div>
            </div>
            <div
              className={`flex flex-col items-center justify-between space-x-2 md:flex-row ${classnames(
                {
                  'opacity-40': !active,
                }
              )}`}
            >
              <div className="w-full px-1 py-2 md:w-1/2">
                <span className={'whitespace-nowrap'}>Billing address:</span>
              </div>
              <div className={'w-full md:w-1/2'}>
                {availableAddresses?.length <= 0 && (
                  <span className={'text-sm'}>{`${subscription.billing_address.street_1}`}</span>
                )}
                {availableAddresses?.length > 0 && findAddress(AddressTypes.BILLING)?.id && (
                  <Select
                    id={'subscriptionBillingAddress'}
                    label={'Address'}
                    value={findAddress(AddressTypes.BILLING)?.id}
                    onBlur={() => null}
                    disabled={!active}
                    onChange={(address) =>
                      handleSubscriptionAddress({
                        action: AddressTypes.BILLING,
                        subscription,
                        address: availableAddresses[address.target.selectedIndex],
                      })
                    }
                    className={`cursor-pointer appearance-none bg-transparent text-sm hover:shadow-md`}
                  >
                    {findAddress(AddressTypes.BILLING)?.street_1 && (
                      <option
                        disabled={true}
                        key={findAddress(AddressTypes.BILLING)?.id}
                        value={findAddress(AddressTypes.BILLING)?.id}
                      >
                        {`${findAddress(AddressTypes.BILLING)?.street_1}, ${
                          findAddress(AddressTypes.BILLING)?.street_2
                        }`}
                      </option>
                    )}
                    {availableAddresses?.map((address) => (
                      <option
                        key={address.id}
                        value={address.id}
                      >{`${address.address1}, ${address.address2}`}</option>
                    ))}
                  </Select>
                )}
              </div>
            </div>
            <HorizontalLine />
            <div className="flex justify-between">
              <OutlineButton
                buttonSize="small"
                onClick={() => {
                  if (subscription?.active) {
                    handleModal({
                      id: subscription.id,
                      type: SubscriptionActions.CONFIRM_ACTION,
                      show: true,
                      action: SubscriptionActions.PAUSE,
                    });
                  } else {
                    handleModal({
                      id: subscription.id,
                      type: SubscriptionActions.ACTIVATE_SUBSCRIPTION,
                      show: true,
                    });
                  }
                }}
              >
                <span className="normal-case">{`${
                  subscription?.active ? 'Pause' : 'Activate'
                } subscription`}</span>
              </OutlineButton>

              <button
                className="text-sm font-medium text-sf-blue-700 underline"
                onClick={() => {
                  handleModal({
                    id: subscription.id,
                    type: SubscriptionActions.CONFIRM_ACTION,
                    show: true,
                    action: SubscriptionActions.CANCEL,
                  });
                }}
              >
                Cancel subscription
              </button>
            </div>
            <div ref={changesBlockRef} className={'display-none h-0 opacity-0'}>
              <HorizontalLine />
              <div className={'space-y-4 pt-4'}>
                <div
                  className={
                    'flex flex-col items-center justify-between space-y-6 md:flex-row md:space-y-0'
                  }
                >
                  <div className="flex flex-col space-y-2 text-xs">
                    {hasChanges && getChanges?.added?.length > 0 && (
                      <div className={'flex items-center space-x-2'}>
                        <div className={'flex h-4 w-4 items-center justify-center'}>
                          <CheckboxIcon />
                        </div>
                        <span className="">{getChanges.added.length} added </span>
                      </div>
                    )}
                    {hasChanges && getChanges?.modified?.length > 0 && (
                      <div className={'flex items-center space-x-2'}>
                        <div className={'flex h-4 w-4 items-center justify-center'}>
                          <ReOrderIcon />
                        </div>
                        <span>{getChanges.modified.length} updated </span>
                      </div>
                    )}
                    {hasChanges && getChanges?.removed?.length > 0 && (
                      <div className={'flex items-center space-x-2'}>
                        <div className={'flex h-4 w-4 items-center justify-center'}>
                          <BinIcon className="text-red-900" />
                        </div>
                        <span className="text-red-900">{getChanges.removed.length} removed </span>
                      </div>
                    )}
                    {hasChanges && (
                      <div
                        onClick={resetSubscription}
                        className={
                          'flex cursor-pointer items-center space-x-2 pt-2 text-xs text-gray-500 underline-offset-4 hover:underline'
                        }
                      >
                        <div className={'flex h-4 w-4 items-center justify-center'}>
                          <CrossIcon />
                        </div>
                        <span>Cancel</span>
                      </div>
                    )}
                  </div>
                  <div className={'flex flex-col'}>
                    <div
                      onClick={() =>
                        subscriptionMutation.mutate({
                          baseSubscription,
                          subscription,
                        })
                      }
                    >
                      <FillButton buttonSize="small">
                        <span className="normal-case">Save changes</span>
                      </FillButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionItem;
